import type {
  AllProductCostsData,
  CreateFollowupRequest,
  CreateFollowupResponse,
  DismissProcessMessageResponse,
  DraftFlowInstanceRequest,
  DraftFlowInstanceResponse,
  FlowCompletionSummary,
  FlowDefinitionResponse,
  FlowInstanceMetadataResponse,
  FlowProcessState,
  FollowupSelectionContextResponse,
  RegenerateSnapshotsResponse,
  ReopenFlowInstanceRequest,
  SnapshotRegenerationStatusResponse,
  SubmitFlowInstanceResponse,
} from "@repo/shared/types";
import { del, get, patch, post } from "../_client";

const BASE = "/internal/flow-instances";

// --- Flow Instance CRUD ---

export function getMetadata(id: string) {
  return get<FlowInstanceMetadataResponse>(`${BASE}/${id}/metadata`);
}

export function getFlowDefinition(id: string) {
  return get<FlowDefinitionResponse>(`${BASE}/${id}/flow-definition`);
}

export function updateName(id: string, name: string) {
  return patch<any>(`${BASE}/${id}/name`, { name });
}

/**
 * Submit (draft → submitted). QL-263: `status` in the response is where the
 * process landed once the flow's auto-PDF setting ran — `completed` when no
 * human step exists.
 */
export function complete(id: string) {
  return post<SubmitFlowInstanceResponse>(`${BASE}/${id}/complete`, {});
}

// --- Followup (QL-150 phase 2 — free-scope selection) ---

/** Every agreement + synced account of the client, grouped by goal family, with per-item eligibility. */
export function getFollowupSelectionContext(clientId: string, flowId: string) {
  const params = new URLSearchParams({ clientId, flowId });
  return get<FollowupSelectionContextResponse>(`${BASE}/followup-selection-context?${params}`);
}

/** Create a follow-up session in one of the three scope modes (accounts / agreements / client). */
export function createFollowupSelection(data: CreateFollowupRequest) {
  return post<CreateFollowupResponse>(
    `${BASE}/followup-selection`,
    data satisfies CreateFollowupRequest,
  );
}

// --- Post-submission process ---

export function getProcessState(id: string) {
  return get<FlowProcessState>(`${BASE}/${id}/process`);
}

/** submitted → draft (only while no PDF exists) */
export function processUnlock(id: string) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/unlock`, {});
}

/**
 * QL-292 — any unfinished process step → draft, cleaning up PDFs, pending
 * approvals and signature requests on the way. `reason` lands in the activity log.
 */
export function processDraft(id: string, reason?: string | null) {
  return post<DraftFlowInstanceResponse>(`${BASE}/${id}/process/draft`, {
    reason: reason ?? null,
  } satisfies DraftFlowInstanceRequest);
}

/** draft → cancelled ("cancel advice"); reason per the flow's cancelReasonMode */
export function processCancel(id: string, reason?: string | null) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/cancel`, { reason: reason ?? null });
}

/** cancelled → draft */
export function processReopen(id: string, reason?: string | null) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/reopen`, {
    reason: reason ?? null,
  } satisfies ReopenFlowInstanceRequest);
}

/**
 * QL-399 — hide one feedback message for everyone on the flow. Idempotent, and
 * available to read-only viewers: it closes a card, it does not edit the advice.
 */
export function dismissProcessMessage(id: string, messageId: string) {
  return post<DismissProcessMessageResponse>(
    `${BASE}/${id}/process/messages/${messageId}/dismiss`,
    {},
  );
}

/** pdf_generated → next enabled step (without regenerating the PDF) */
export function processAdvance(id: string) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/advance`, {});
}

export function complianceApprove(id: string) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/compliance/approve`, {});
}

export function complianceReject(id: string, comment: string) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/compliance/reject`, { comment });
}

export function complianceWithdraw(id: string) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/compliance/withdraw`, {});
}

export function externalWithdraw(id: string) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/external/withdraw`, {});
}

/** Back out of the signing step (only when no signature order exists). */
export function signingBack(id: string) {
  return post<{ success: boolean }>(`${BASE}/${id}/process/signing/back`, {});
}

/**
 * Flow-completion preflight — current % + missing[] for the advisor wizard's progress bar.
 * Backed by GET /internal/flow-instances/:id/completion (engine in apps/api).
 */
export function getFlowCompletion(id: string) {
  return get<FlowCompletionSummary>(`${BASE}/${id}/completion`);
}

export function regenerateSnapshots(id: string) {
  return post<RegenerateSnapshotsResponse>(`${BASE}/${id}/regenerate-snapshots`);
}

/** QL-675 — is a snapshot rebuild queued or running; polled while one is. */
export function getSnapshotStatus(id: string) {
  return get<SnapshotRegenerationStatusResponse>(`${BASE}/${id}/snapshot-status`);
}

// --- Snapshot Data ---

export function getProductCosts(instanceId: string) {
  return get<AllProductCostsData>(`${BASE}/${instanceId}/snapshots/product-costs`);
}

// --- Advisor Notes ---

export function getAdvisorNote(instanceId: string, pageId: string, goalId?: string) {
  const params = new URLSearchParams();
  if (pageId) params.set("pageId", pageId);
  if (goalId) params.set("goalId", goalId);
  return get<{ notes: string | null }>(`${BASE}/${instanceId}/advisor-notes?${params}`);
}

export function saveAdvisorNote(
  instanceId: string,
  data: {
    pageId: string;
    goalId?: string;
    notes: string;
  },
) {
  return post<{ success: boolean }>(`${BASE}/${instanceId}/advisor-notes`, data);
}

// --- Public Access ---

// --- Signatures ---

export function getSignatures(instanceId: string) {
  return get<any>(`${BASE}/${instanceId}/signatures`);
}

/** Cancel (delete) a signature order, freeing the signing step to move back. */
export function cancelSignatureOrder(orderId: string) {
  return del<{ success: boolean; error?: string }>(`${BASE}/signatures/${orderId}`);
}
